import { zodResolver } from '@hookform/resolvers/zod'
import { FormProvider, useForm } from 'react-hook-form'
import { useQuery } from 'react-query'

import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import { FormSelect } from '@/components/ui/form/FormSelect.tsx'
import type { FormValues } from '@/dialogs/MoreTikTokVideoOrSoundsDialog/validation.ts'
import {
  FORM_ID,
  listSelectionFormSchema,
} from '@/dialogs/MoreTikTokVideoOrSoundsDialog/validation.ts'
import type { DialogProps } from '@/dialogs/types'
import { useListMutations } from '@/modules/tiktok-research/hooks/useListMutations.ts'
import type { SelectedListItemsMap } from '@/modules/tiktok-research/lists/types.ts'
import { getLists } from '@/modules/tiktok-research/service'
import { getListsOptions } from '@/modules/tiktok-research/utils.tsx'
import type { TikTokContentListType } from '@/modules/tiktok-research/videos/ui/types'
import { handleError } from '@/utils/error'

interface Props extends DialogProps {
  currentList: TikTokContentListType
  selectedItems: SelectedListItemsMap
  onActionSuccess: () => void
}

export const MoreTikTokVideoOrSoundsDialog = ({
  isOpen,
  onClose,
  currentList,
  selectedItems,
  onActionSuccess,
}: Props) => {
  const { moveItemsToOtherList } = useListMutations()

  const listsQuery = useQuery('lists', getLists, {
    onError: (error) =>
      handleError(error, 'Failed to get lists', listsQuery.refetch),
  })

  const listsOptions = getListsOptions(listsQuery.data ?? [])
  const form = useForm<FormValues>({
    resolver: zodResolver(listSelectionFormSchema),
    mode: 'onChange',
    defaultValues: {
      fromList: String(currentList.id),
      toList: String(currentList.id),
    },
  })
  const handleSubmit = async ({ toList, fromList }: FormValues) => {
    await moveItemsToOtherList.mutateAsync({
      fromListId: Number(fromList),
      toListId: Number(toList),
      itemsIds: {
        sounds: Array.from(selectedItems.sounds.keys()),
        posts: Array.from(selectedItems.posts.keys()),
      },
    })

    onClose()
    onActionSuccess()
  }

  return (
    <Dialog
      show={isOpen}
      onClose={onClose}
      panelClassName="overflow-visible"
      bigDialog
    >
      <Dialog.Header>
        <Dialog.Title className="text-center text-3xl font-semibold leading-9">
          Move Videos or Sound
        </Dialog.Title>
      </Dialog.Header>
      <Dialog.Description>
        <form
          id={FORM_ID}
          onSubmit={form.handleSubmit(handleSubmit)}
        >
          <FormProvider {...form}>
            <FormSelect
              label="Current List"
              name="toList"
              placeholder="Select List"
              options={listsOptions}
              disabled={listsQuery.isLoading}
            />
          </FormProvider>
        </form>
      </Dialog.Description>
      <Dialog.ClosableFooter onClose={onClose}>
        <Button
          variant="default"
          form={FORM_ID}
          isLoading={form.formState.isSubmitting}
          disabled={!form.formState.isValid}
        >
          Save
        </Button>
      </Dialog.ClosableFooter>
    </Dialog>
  )
}
